import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { TenderCase, TenderRun } from "../types"
import type {
  TenderCaseApi,
  TenderCaseListItemApi,
  TenderParticipant,
  TenderParticipantRef,
  TenderParticipantRole,
  TenderPersonRef,
} from "@/api/tender-api"
import type { QueryClient } from "@tanstack/react-query"

import {
  addTenderParticipant,
  createTenderCase,
  deleteTenderCase,
  getTenderHandoffCc,
  getTenderHandoffConfig,
  handoffTenderCase,
  joinTenderCase,
  leaveTenderCase,
  listTenderCases,
  listTenderParticipants,
  markTenderNotificationViewed,
  previewTenderHandoffEmail,
  quickStartTenderCase,
  removeTenderParticipant,
  startTenderAutoPipeline,
  transferTenderOwnership,
  updateTenderParticipantRole,
} from "@/api/tender-api"

import { useTenderPaths } from "./use-tender-paths"
import {
  isActiveAutoPipelineStatus,
  isProcessingStepStatus,
} from "../components/tender-status-utils"
import { isInvolvedCase } from "../utils/is-involved-case"

export const TENDER_QUERY_KEYS = {
  cases: ["tenderCases"] as const,
  case: (caseId: string) => ["tenderCase", caseId] as const,
  // Prefixes for invalidating every keyed query of a kind (partial match).
  casePrefix: ["tenderCase"] as const,
  participantsPrefix: ["tenderParticipants"] as const,
  cvMatchPrefix: ["tenderCvMatch"] as const,
  extraction: (caseId: string, runId: string) => ["tenderExtraction", caseId, runId] as const,
  llmTrace: (caseId: string, runId: string, step: string) =>
    ["tenderLlmTrace", caseId, runId, step] as const,
  participants: (caseId: string) => ["tenderParticipants", caseId] as const,
}

/** Map the full case API shape (used by the detail page) to the frontend TenderCase type. */
export function apiCaseToTenderCase(api: TenderCaseApi): TenderCase {
  return {
    ...api,
    submissionDeadline: api.submissionDeadline ?? undefined,
    submissionDeadlineIsManual: api.submissionDeadlineIsManual,
    runs: api.runs.map(
      (r): TenderRun => ({
        ...r,
        bidDocuments: r.bidDocuments,
        cvDocuments: r.cvDocuments,
        overrideDocumentIds: r.overrideDocumentIds,
        requirements: [],
        evaluationCriteria: [],
        contractTerms: [],
        criticalDates: [],
        requirementMatrix: [],
        risks: [],
        scorecardDimensions: [],
        roleRequirements: [],
        bidDeliverables: [],
        recommendation: null,
        bidReview: undefined,
        extractionStatus: r.extractionStatus ?? null,
        bidReviewStatus: r.bidReviewStatus ?? null,
      })
    ),
  }
}

// Poll only for cases the current user is involved in: the list is org-wide, so
// counting everyone's cases made every open tab poll every 5 s whenever any case
// anywhere was processing (or stuck), which throttled Cosmos (#3418).
function hasProcessingPipeline(items: TenderCaseListItemApi[]): boolean {
  return items.some(
    (item) =>
      isInvolvedCase(item) &&
      (isProcessingStepStatus(item.extractionStatus) ||
        isProcessingStepStatus(item.bidReviewStatus) ||
        isProcessingStepStatus(item.cvMatchStatus) ||
        isActiveAutoPipelineStatus(item.autoPipelineStatus))
  )
}

type UseTenderCasesOptions = {
  /** When false, never poll the list (e.g. case detail page relies on useTenderCase). */
  pollWhileProcessing?: boolean
  /** Override the default 5s poll interval while any case is processing. */
  pollIntervalMs?: number
}

export function useTenderCases(options?: UseTenderCasesOptions) {
  const pollWhileProcessing = options?.pollWhileProcessing ?? true
  const pollIntervalMs = options?.pollIntervalMs ?? 5_000
  return useQuery({
    queryKey: TENDER_QUERY_KEYS.cases,
    queryFn: listTenderCases,
    refetchInterval: (query) => {
      if (!pollWhileProcessing) return false
      const data = query.state.data
      if (!data) return false
      return hasProcessingPipeline(data) ? pollIntervalMs : false
    },
  })
}

export function useCreateTenderCase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (title: string) => createTenderCase(title),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases }),
  })
}

export function useQuickStartTenderCase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      title?: string
      cvLibraryId?: string
      procuringAuthority?: string
      sourceDocumentsUrl?: string
      submissionDeadline?: string
      questionsDeadline?: string
      responsible?: TenderPersonRef
      participants?: TenderParticipantRef[]
      files: File[]
      overrideFileIndexes?: number[]
      onUploadProgress?: (progress: { loaded: number; total: number }) => void
    }) => quickStartTenderCase(params),
    // Invalidate on error as well: a failed or ambiguous start leaves a real,
    // existing case — refresh the list so it is visible instead of hidden
    // until reload.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useRetryTenderAutoPipelineStart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      caseId: string
      runId: string
      cvLibraryId?: string
      overrideDocIds: string[]
    }) => startTenderAutoPipeline(args),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

/**
 * A delete that fails part-way can already have removed some of a case's
 * records, so after any attempt every cached query of the case is marked
 * stale. Nothing refetches now; whatever shows the case next loads it fresh.
 */
function markCaseQueriesStale(queryClient: QueryClient, caseIds: readonly string[]) {
  void queryClient.invalidateQueries({
    predicate: ({ queryKey: [kind, caseId] }) =>
      typeof kind === "string" &&
      kind.startsWith("tender") &&
      typeof caseId === "string" &&
      caseIds.includes(caseId),
    refetchType: "none",
  })
}

export function useDeleteTenderCase() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) => deleteTenderCase(caseId),
    onSettled: (_data, _error, caseId) => {
      markCaseQueriesStale(queryClient, [caseId])
      // Refresh after a failure too: a case deleted elsewhere drops out of the list.
      return queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
    onError: () => {
      toast.error(t("tender.dialogs.deleteFailed"))
    },
  })
}

/** A bulk delete that left some cases behind; `failedIds` still exist. */
export class TenderCasesDeleteError extends Error {
  readonly failedIds: string[]

  constructor(failedIds: string[], total: number) {
    super(`Could not delete ${failedIds.length} of ${total} tender cases`)
    this.name = "TenderCasesDeleteError"
    this.failedIds = failedIds
  }
}

export function useDeleteTenderCases() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    // Wait for every delete, so one failure can't hide the ones that went through.
    mutationFn: async (caseIds: string[]) => {
      const results = await Promise.allSettled(caseIds.map((id) => deleteTenderCase(id)))
      const failedIds = caseIds.filter((_, index) => results[index].status === "rejected")
      if (failedIds.length > 0) throw new TenderCasesDeleteError(failedIds, caseIds.length)
    },
    onSettled: (_data, _error, caseIds) => {
      markCaseQueriesStale(queryClient, caseIds)
      return queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
    onError: () => {
      toast.error(t("tender.dialogs.deleteManyFailed"))
    },
  })
}

// ── Participant hooks ──────────────────────────────────────────────────────────

export function useTenderParticipants(caseId: string) {
  return useQuery({
    queryKey: TENDER_QUERY_KEYS.participants(caseId),
    queryFn: () => listTenderParticipants(caseId),
    enabled: !!caseId,
    staleTime: 1000 * 60 * 2,
  })
}

export function useTenderHandoffCc(caseId: string, enabled = true) {
  return useQuery({
    queryKey: [...TENDER_QUERY_KEYS.participants(caseId), "handoffCc"],
    queryFn: () => getTenderHandoffCc(caseId),
    enabled: !!caseId && enabled,
    // Reverify when the dialog opens: a saved participant may have been disabled.
    staleTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function useAddTenderParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, participant }: { caseId: string; participant: TenderParticipant }) =>
      addTenderParticipant(caseId, participant),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.participants(variables.caseId),
      })
    },
  })
}

export function useUpdateTenderParticipantRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      caseId,
      participantOid,
      role,
    }: {
      caseId: string
      participantOid: string
      role: TenderParticipantRole
    }) => updateTenderParticipantRole(caseId, participantOid, role),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.participants(variables.caseId),
      })
    },
  })
}

export function useRemoveTenderParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, participantOid }: { caseId: string; participantOid: string }) =>
      removeTenderParticipant(caseId, participantOid),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.participants(variables.caseId),
      })
    },
  })
}

export function useTransferTenderOwnership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, newOwnerOid }: { caseId: string; newOwnerOid: string }) =>
      transferTenderOwnership(caseId, newOwnerOid),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.participants(variables.caseId),
      })
    },
  })
}

export function useJoinTenderCase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) => joinTenderCase(caseId),
    onSuccess: (_, caseId) => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.participants(caseId),
      })
    },
  })
}

export function useTenderHandoffConfig() {
  return useQuery({
    queryKey: ["tenderHandoffConfig"] as const,
    queryFn: getTenderHandoffConfig,
    staleTime: 5 * 60 * 1000,
  })
}

export function useHandoffTenderCase(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Parameters<typeof handoffTenderCase>[1]) =>
      handoffTenderCase(caseId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.participants(caseId) })
    },
  })
}

// Preview is read-only on the server, so nothing is invalidated on success.
export function usePreviewTenderHandoffEmail(caseId: string) {
  return useMutation({
    mutationFn: (payload: Parameters<typeof previewTenderHandoffEmail>[1]) =>
      previewTenderHandoffEmail(caseId, payload),
  })
}

export function useLeaveTenderCase() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { basePath } = useTenderPaths()
  return useMutation({
    mutationFn: (caseId: string) => leaveTenderCase(caseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
      void navigate(basePath)
    },
  })
}

export function useMarkTenderNotificationViewed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) => markTenderNotificationViewed(caseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}
